import {
  ActivityDetailDto,
  ActivityFeedDto,
  ActivityListItemDtoPagedResult,
  GetMeActivityCurrentV1Params,
  GetMeActivityPastV1Params,
} from '@/services/platform/api/model';
import {
  getMeActivityByIdV1,
  getMeActivityCurrentV1,
  getMeActivityPastV1,
} from '../../api/me-—-activity/me-—-activity';

/**
 * Fetches a single activity (order or reservation) by ID for the authenticated user.
 *
 * The backend resolves which branch of `ActivityDetailDto` is populated (order or
 * reservation) from the ID — the caller does not need to supply a type discriminator.
 * A user can only access their own activities; requests for IDs belonging to other
 * users are rejected with 404.
 *
 * @param id - The activity's public GUID.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns `ActivityDetailDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated), 404 (not found or not owned by
 *   the current user), or network error.
 */
export async function getActivityById(id: string, locale: string): Promise<ActivityDetailDto | undefined> {
  try {
    const response = await getMeActivityByIdV1(id, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      return data as ActivityDetailDto;
    }

    console.warn(`getActivityById: unexpected status ${status} for id "${id}"`);
    return undefined;
  } catch (error) {
    console.warn(`getActivityById: request failed for id "${id}":`, error);
    return undefined;
  }
}

/**
 * Fetches the current activity tab (active shop orders) for the authenticated user.
 *
 * Results are paginated — use `page` and `pageSize` to navigate through large sets.
 * Defaults applied by the API: `page = 1`, `pageSize = 20` (max 100).
 *
 * @param params - Optional pagination params: `page` and `pageSize`.
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns `ActivityListItemDtoPagedResult` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getCurrentActivity(
  params?: GetMeActivityCurrentV1Params,
  locale?: string
): Promise<ActivityListItemDtoPagedResult | undefined> {
  try {
    const response = await getMeActivityCurrentV1(params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      return data as ActivityListItemDtoPagedResult;
    }

    console.warn(`getCurrentActivity: unexpected status ${status}`);
    return undefined;
  } catch (error) {
    console.warn('getCurrentActivity: request failed:', error);
    return undefined;
  }
}

/**
 * Fetches the past activity feed (finished orders and reservations) for the authenticated user.
 *
 * Uses cursor-based pagination: pass the `before` cursor from a previous response to advance
 * through older pages. Omit `before` to load the most recent page.
 *
 * @param params - Optional cursor params:
 *   - `before` — ISO-8601 datetime cursor; returns items on or before this date.
 *   - `limit` — number of items to return (default: 20, max: 100).
 * @param locale - BCP 47 locale tag (e.g. `"en-GB"`, `"pt-PT"`) forwarded as
 *   `Accept-Language` so the API returns content in the correct language.
 * @returns `ActivityFeedDto` on HTTP 200.
 * @returns `undefined` on 401 (unauthenticated) or network error.
 */
export async function getPastActivity(
  params?: GetMeActivityPastV1Params,
  locale?: string
): Promise<ActivityFeedDto | undefined> {
  try {
    const response = await getMeActivityPastV1(params, {
      headers: locale ? { 'Accept-Language': locale } : {},
    });
    const { status, data } = response as { status: number; data: unknown };

    if (status === 200) {
      return data as ActivityFeedDto;
    }

    console.warn(`getPastActivity: unexpected status ${status}`);
    return undefined;
  } catch (error) {
    console.warn('getPastActivity: request failed:', error);
    return undefined;
  }
}
